import Chapter, { FadeIn } from "./Chapter";

const VARS = [
  { k: "Al Pastor", v: "/10", note: "The trompo test. Non-negotiable." },
  { k: "Carne Asada", v: "/10", note: "Smoke, char, and marinade depth." },
  { k: "Chicken", v: "/10", note: "The hardest protein to care about." },
  { k: "Salsa", v: "/10", note: "Freshness, complexity, housemade vs. generic." },
  { k: "Tortilla", v: "/10", note: "Handmade vs. store-bought. Texture. Consistency." },
  { k: "Specialty", v: "/10", note: "Birria, menudo, barbacoa — the differentiators." },
];

export const Intro = () => (
  <>
    <Chapter no="01" kicker="Introduction" title="Why Tacos, Why This Matters" id="why" />
    <section className="px-4 sm:px-8 pb-24">
      <div className="grid lg:grid-cols-12 gap-10">
        <FadeIn className="lg:col-span-7 space-y-6 font-body text-base md:text-lg leading-relaxed text-ink2 max-w-2xl">
          <p className="font-serif italic text-2xl md:text-3xl leading-snug text-ink border-l-2 border-salsa pl-6" data-testid="intro-pullquote">
            "Why does the truck parked outside a bar on South Congress at midnight taste better than
            the restaurant that just got written up in the New York Times?"
          </p>
          <p>
            The difference is not luck. It is process, culture, and the relentless application of
            techniques passed down through generations — charring tomatoes for salsa, marinating meat
            for twenty-four hours, pressing tortillas by hand every single morning. These are not
            aesthetic choices. They are what a memorable taco is made of.
          </p>
          <p>
            But Austin's taco culture is at risk of being buried by a different kind of taco economy —
            one driven by Instagram aesthetics, paid influencer campaigns, and the algorithmic
            amplification of mediocrity. A taqueria with a marketing budget can gain more visibility
            in six months than a family truck that has been serving for fifteen years.
          </p>
          <p>
            This index exists to correct that imbalance: one consistent, transparent scoring framework
            applied to every taco the same way — regardless of who wrote about it, or who was paid to.
          </p>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-5">
          <div className="grid grid-cols-2 border-2 border-ink shadow-[8px_8px_0_rgba(26,26,26,0.9)]" data-testid="intro-stats">
            {[
              ["17", "Spots visited"],
              ["51", "Tacos scored"],
              ["5", "Neighborhoods covered"],
              ["3", "Platforms cross-checked"],
            ].map(([n, l], i) => (
              <div key={l} className={`p-6 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""} border-ink`}>
                <p className="font-serif font-semibold text-4xl sm:text-5xl">{n}</p>
                <p className="mt-2 font-mono text-[9px] tracking-[0.18em] uppercase text-muted2">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-muted2">
            Fig. 02 — The field work, by the numbers.
          </p>
        </FadeIn>
      </div>
    </section>

    <Chapter no="02" kicker="Methodology" title="One Framework, Every Taco" id="methodology" />
    <section className="px-4 sm:px-8 pb-24">
      <FadeIn className="max-w-2xl font-body text-base md:text-lg leading-relaxed text-ink2 mb-10">
        <p>
          Seventeen authentic Mexican spots across East, South, North, Central, and campus Austin.
          Chains and nationally marketed spots — Torchy's, Tacodeli — were excluded on principle.
          Each protein is scored out of 10, composited to 30, and averaged back to a final score out
          of 10. Personal scores were then cross-verified against Google, Yelp, and the Infatuation
          to measure the <span className="text-ink font-medium">Authenticity Gap</span>: the distance
          between online perception and actual quality.
        </p>
      </FadeIn>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t-2 border-l-2 border-ink" data-testid="methodology-grid">
        {VARS.map((v, i) => (
          <FadeIn key={v.k} delay={i * 0.05} className="border-b-2 border-r-2 border-ink p-6">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted2 mb-2">
              Variable {String(i + 1).padStart(2, "0")}
            </p>
            <p className="font-serif font-semibold text-2xl">
              {v.k} <span className="text-salsa font-mono text-sm align-top">{v.v}</span>
            </p>
            <p className="mt-2 font-mono text-xs text-ink2">{v.note}</p>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={0.1} className="mt-10 border-2 border-salsa p-6 sm:p-8 max-w-3xl" data-testid="multiplier-hypothesis">
        <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-salsa mb-3">The Central Hypothesis</p>
        <p className="font-serif text-2xl md:text-3xl leading-snug">
          Salsa and tortilla quality function as <em className="text-salsa">multiplier</em> variables —
          not additive ones. The same meat on a handmade tortilla with housemade salsa is a different
          taco. This distinction separates authentic taquerias from marketed competitors.
        </p>
      </FadeIn>
    </section>
  </>
);

export default Intro;
